// packages/ui/src/api/client.ts
// Fetch wrapper with auth token injection for dashboard API

const TOKEN_KEY = 'droid_token'

export interface ApiError {
  status: number
  message: string
}

export class ApiRequestError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.name = 'ApiRequestError'
    this.status = status
  }
}

function getBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL ?? '/api/dashboard'
}

function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export async function apiRequest<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const url = `${getBaseUrl()}${path}`
  const token = getToken()

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const res = await fetch(url, {
    ...options,
    headers,
  })

  if (res.status === 204) {
    return undefined as T
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }))
    throw new ApiRequestError(res.status, body.error ?? 'Request failed')
  }

  return res.json() as Promise<T>
}

// ─── Typed API methods ───────────────────────────────────────

export interface DashboardCard {
  id: string
  title: string
  description: string
  column: string
  priority: 'low' | 'medium' | 'high' | 'critical'
  source: 'chat' | 'cron' | 'webhook' | 'manual' | 'discovery'
  targetHosts: string[]
  plan?: string
  result?: string
  error?: string
  retryCount: number
  maxRetries: number
  createdAt: string
  updatedAt: string
  completedAt?: string
  metadata: Record<string, unknown>
}

export interface DashboardSession {
  id: string
  channel: string
  userId: string
  state: string
  tokenCount: number
  createdAt: string
  lastActive: string
  messages?: unknown[]
  events?: AuditEvent[]
}

export interface AuditEvent {
  id: string
  eventType: string
  sessionId?: string
  tool?: string
  params?: Record<string, unknown>
  result?: unknown
  createdAt: string
}

export interface CardsResponse {
  cards: DashboardCard[]
}

export interface CardResponse {
  card: DashboardCard
}

export interface SessionsResponse {
  sessions: DashboardSession[]
  total: number
  limit: number
  offset: number
}

export interface SessionResponse {
  session: DashboardSession
}

export interface ActivityResponse {
  entries: AuditEvent[]
  total: number
  limit: number
  offset: number
}

export interface CreateCardInput {
  title: string
  description?: string
  priority?: DashboardCard['priority']
  source?: DashboardCard['source']
  targetHosts?: string[]
}

export interface UpdateCardInput {
  id: string
  title?: string
  description?: string
  priority?: DashboardCard['priority']
  status?: string
}

// ─── API functions ───────────────────────────────────────────

export function fetchCards(filters?: {
  status?: string
  priority?: string
  source?: string
}): Promise<CardsResponse> {
  const params = new URLSearchParams()
  if (filters?.status) params.set('status', filters.status)
  if (filters?.priority) params.set('priority', filters.priority)
  if (filters?.source) params.set('source', filters.source)
  const qs = params.toString()
  return apiRequest<CardsResponse>(`/cards${qs ? `?${qs}` : ''}`)
}

export function createCard(input: CreateCardInput): Promise<CardResponse> {
  return apiRequest<CardResponse>('/cards', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function updateCard(input: UpdateCardInput): Promise<CardResponse> {
  const { id, ...fields } = input
  return apiRequest<CardResponse>(`/cards/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(fields),
  })
}

export function deleteCard(id: string): Promise<void> {
  return apiRequest<void>(`/cards/${id}`, { method: 'DELETE' })
}

export function fetchSessions(opts: {
  limit?: number
  offset?: number
}): Promise<SessionsResponse> {
  const params = new URLSearchParams()
  if (opts.limit != null) params.set('limit', String(opts.limit))
  if (opts.offset != null) params.set('offset', String(opts.offset))
  return apiRequest<SessionsResponse>(`/sessions?${params}`)
}

export function fetchSession(id: string): Promise<SessionResponse> {
  return apiRequest<SessionResponse>(`/sessions/${id}`)
}

export function fetchActivity(opts: {
  limit?: number
  offset?: number
}): Promise<ActivityResponse> {
  const params = new URLSearchParams()
  if (opts.limit != null) params.set('limit', String(opts.limit))
  if (opts.offset != null) params.set('offset', String(opts.offset))
  return apiRequest<ActivityResponse>(`/activity?${params}`)
}
